import _ from 'lodash'
import { interval, Observable, of, ReplaySubject, zip } from 'rxjs'
import { catchError, concatMap, first, map, reduce, switchMap, tap } from 'rxjs/operators'

import { convertCachedWemiToWemi } from './utils'
import { 
    SearchResponse, 
    SearchResult, 
    Item, 
    Wemi, 
    SynchroMediaUpdates, 
    PushCachedValueResult, 
    MEDIA_SUBJECTS_KEYS,
    DataStoreOptions,
    AssociatedMedias,
    SearchQuery,
    ReplaySubjectContent,
} from '@skinsoft/types'
import { wemiSelectors } from '@skinsoft/selectors'


import { OfflineDataFetcher, CACHE_KEYS, ItemOfflineDataFetcher } from '@skinsoft/offline'
import { API_ENDPOINTS } from '@skinsoft/network'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import { searchResultSelectors } from '@skinsoft/search-data-store'
import { isWemi, WemiUtils } from '@skinsoft/wemi-utils'
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { ID_STATUS_CREATED_ON_SKIN_REPORTER, cleanRefs, cleanValueToSort, fixEmptyObjectsInSubElements } from '@skinsoft/utils'

declare const config
const ITEM_SEARCH_TYPES: string[] = config.item_search_types

const SEARCH_PAGE_SIZE: number = 1000

// @ts-ignore
export class ItemDataStore extends EntityDataStore<Item, any, SearchResult | Wemi> {
    public static getInstance() {
        if (!ItemDataStore.instance) {
            ItemDataStore.instance = new ItemDataStore();
        }

        return ItemDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$itemDataStore = ItemDataStore.getInstance();
    }

    public static getItemSearchResultId(itemSearchResult: SearchResult | Wemi) {
        if ("id" in itemSearchResult) {
            return searchResultSelectors.id(itemSearchResult);
        } else {
            return wemiSelectors.id(itemSearchResult);
        }
    }

    private static instance: ItemDataStore = null as any;

    constructor() {
        super('items', ITEM_SEARCH_TYPES)
    }

    public exists(id: Item['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public searchAll(searchQuery: SearchQuery, pathAndValueToCheck?: any, 
        searchEndpointOverride: string | null = null, options: DataStoreOptions = {}, onlyOnLocallyCreated: boolean = false, forceSearchTextId: boolean = false)
        : Observable<SearchResponse<SearchResult | Wemi>> {

        if(!searchQuery.pageSize) searchQuery.pageSize = 15
        if(!searchQuery.page) searchQuery.page = 0

        return super.searchAll(searchQuery, pathAndValueToCheck, searchEndpointOverride, options, onlyOnLocallyCreated, forceSearchTextId)
    }

    public mergeWemi(data): SearchResponse<SearchResult | Wemi> {
        return {...data, allData: _.mergeWith(data[1].allData, data[0], (item, wemi) => {
            if(item && isWemi(item)) return item

            const workTitles = wemi.work?.descriptionMuseumWorkSchema?.workTitles;

            return {
                ...wemi,
                item,
                work: {
                    ...wemi.work,
                    indexTitle: (workTitles && workTitles.length) ? (workTitles[0]?.title) : undefined
                }
            }
        })}
    }

    private handleImageReplaySubject(imageSubject: ReplaySubject<ReplaySubjectContent>, wemi: Wemi, dataToPush: SynchroMediaUpdates<Item>) {
        const localNewFirstMedia = dataToPush.cachedMedias.first

        if(imageSubject && wemi.item) {
            const timer = interval(1000)
            const imageSubjectWaiter$ = zip(imageSubject, timer).pipe(map(([a, _]) => a))

            imageSubjectWaiter$.pipe(
                switchMap(replaySubjectContent => {
                    if(replaySubjectContent.newMediaId) {
                        const observableArray: Observable<any>[] = []

                        try{
                            ImageTransferHelper.getInstance().addImageToReporterSubAlbum(replaySubjectContent.newMediaId)
                        } catch(err) {
                            // TODO handle errors while adding image
                        }
                        return zip(
                            of(replaySubjectContent),
                            this.addImage(wemi.item!, replaySubjectContent as { newMediaId: string })
                        )
                    }
                    
                    throw new Error("Failed to upload image")
                }),
                map(([replaySubjectContent, addImgRes]) => {
                    return replaySubjectContent
                }),
                reduce((newFirstMediaId: any, replaySubjectContent: any) => {
                    try {
                        if(replaySubjectContent.newMediaId) {
                            ImageTransferHelper.getInstance().deleteImageInCache(replaySubjectContent.newMediaId as string)
                        }
    
                        if(replaySubjectContent.cacheKeys) {
                            for(const cacheKey of replaySubjectContent.cacheKeys) {
                                ImageTransferHelper.getInstance().deleteImageInCache(cacheKey)
            
                                // we register the id of the new first media now that it has been created (to update it later)
                                if(localNewFirstMedia && cacheKey === localNewFirstMedia) {
                                    return replaySubjectContent.newMediaId
                                }
                            }
                        }

                        if(newFirstMediaId) {
                            return newFirstMediaId
                        }
                        return undefined
                    } catch(err) {
                        // TODO handle errors while adding image
                        if(newFirstMediaId) {
                            return newFirstMediaId
                        }
                        return undefined
                    }
                }, undefined)
            )
            .subscribe(
                async (newFirstMediaId) => {
                    // we reupdate the item to correct the first media if necessary
                    if(newFirstMediaId) {
                        const itemId = wemi.item!.id
                        const fullItem = await this.fetchOne(itemId).toPromise()

                        fullItem.associatedMedias.first = newFirstMediaId
                        fullItem.associatedMedias.medias.push(newFirstMediaId)
                        fullItem.associatedMedias.medias = _.uniq(fullItem.associatedMedias.medias)

                        this.update(itemId, fullItem, undefined, true).subscribe()
                    }
                },
                (err) => {},
                () => {}
            )
        }        
    }

    private pushCachedCreationOrUpdate(dataToPush: SynchroMediaUpdates<Item>, item: Item): Observable<PushCachedValueResult> {
        this.synchronizationMap[dataToPush.id] = 'inProgress'
        
        return WemiUtils.getWemiFromItemOnLine(
            item.id,
        )
            .pipe(
                switchMap(originalWemi => {
                    return zip(
                        of(originalWemi),
                        this.getWemiCachedUpdates(dataToPush.body)
                    )
                }),
                switchMap(wemis => {
                    const originalWemi = wemis[0]
                    let updatedWemi = convertCachedWemiToWemi(wemis[1])

                    let mergedWemi = WemiUtils.mergeUpdatedWemiWithOriginalWemi(originalWemi, updatedWemi)
                    
                    if(originalWemi.item?.identificationItemSchema?.identifiers?.[0]?.identifier) {
                        _.set(
                            mergedWemi,
                            "item.identificationItemSchema.identifiers[0].identifier",
                            originalWemi.item.identificationItemSchema.identifiers[0].identifier
                        )
                    }

                    if(mergedWemi.item?.associatedMedias.first && MediaTransferHelper.isLocalMedia(mergedWemi.item?.associatedMedias.first)) {
                        _.set(mergedWemi, 'item.associatedMedias.first', null)
                    }
                
                    try {
                        fixEmptyObjectsInSubElements(mergedWemi)
                    } catch(err) {
                        // logger indicates that it happened in ItemDataStore.handleSynchroUpdateFromCache
                        throw err
                    }
                    
                    return of(mergedWemi)
                }),
                switchMap(wemi => {
                    return WemiUtils.saveWemi(wemi, true)
                }),
                switchMap(res => {
                    this.synchronizationMap[dataToPush.id] = item.id

                    return WemiUtils.getWemiFromItem(item.id)
                }),
                tap((async wemi => {
                    const imageSubject = dataToPush.mediaSubjects[MEDIA_SUBJECTS_KEYS.image]
                    this.handleImageReplaySubject(imageSubject, wemi, dataToPush)
                })),
                first(),
                // Handle success or error if the update and emit boolean
                catchError(e => {
                    return of(Error('Error during update...'))
                }),
                switchMap(response => {
                    if (!(response instanceof Error)) {
                        return of({ success: true })
                    }
                    return of({ success: false })
                }),
            ) as Observable<PushCachedValueResult>
    }

    protected pushCachedUpdate(dataToUpdate): Observable<PushCachedValueResult> {
        if (dataToUpdate.id && dataToUpdate.body) {
            return this.fetchOne(dataToUpdate.id).pipe(
                switchMap(item => {
                    return this.pushCachedCreationOrUpdate(dataToUpdate, item)
                })
            )
        }
        else {
            return of({ success: true });
        }
    }

    protected pushCachedCreations(dataToCreate): Observable<PushCachedValueResult> {
        if (dataToCreate.id && dataToCreate.body) {
            return this.create().pipe(
                switchMap(item => {
                    return this.pushCachedCreationOrUpdate(dataToCreate, item)
                })
            )
        }
        else {
            return of({ success: true });
        }
    }

    protected cleanCacheAfterPush(cacheKeys: string[]) {
        for(const key of cacheKeys) {
            WemiUtils.getWemiFromItemOffline(key).pipe(
                tap(wemi => {
                    WemiUtils.deleteModificationsWemi(wemi)
                })
            ).subscribe()
        }
    }

    public update(id: string, body: Partial<Item>, endpointOverride?: string, ignoreCachedStatus: boolean = false): Observable<Item> {
        let entityType = body.type ?? body.entityType
        if(entityType) {
            entityType = _.lowerFirst(entityType)
        }
        const endpoint = endpointOverride ?? API_ENDPOINTS[entityType as string]
        
        return super.update(id, body, endpoint, ignoreCachedStatus)
    }

    public getWemiFromItem(itemId: string): Observable<Wemi> {
        if (OfflineDataFetcher.isTemporaryId(itemId)) {
            return WemiUtils.getOrCreateWemiFromItemOffline(itemId)
        }
        return WemiUtils.getWemiFromItem(itemId)
    }

    public getWemiCachedUpdates(item: Item): Observable<Wemi> {
        return WemiUtils.getWemiFromItemOffline(item.id).pipe(
            switchMap(wemi => {
                try {
                    fixEmptyObjectsInSubElements(wemi)
                } catch(err) {
                    throw err
                }
                return of(wemi)
            })
        )
    }

    public create(createBody?: Partial<Item>, body?: Partial<Item>): Observable<Item> {
        const uri = '/physical-museum-items';
        if (window.navigator.onLine || !body) {
            return this.proxy.fetch(uri, createBody, 'POST').pipe(
                concatMap(response => {
                    if (!body) {
                        return of(response);
                    }
                    // In case of a locally created entity, replacing temporary id by the one from db
                    const id: string = _.get(body, "id", '')
                    if (id && OfflineDataFetcher.isTemporaryId(id)) {
                        Object.defineProperty(body, "id", { value: response.id })
                    }
                    _.set(body, 'managementData.entryStatus', ID_STATUS_CREATED_ON_SKIN_REPORTER)
                    return this.update(response.id, _.merge(response, body));
                }),
                switchMap(({ id }) => this.fetchOne(id)),
            );
        } else { // Offline mode
            // the '_' prefix of the id identifies the id as temporary in the cache keys
            return this.createOffline(body).pipe(
                switchMap(item => {
                    if(!(item as any).associatedMedias) {
                        const associatedMedias: AssociatedMedias = {
                            medias: [],
                        }
                        _.set(item as any, 'associatedMedias', associatedMedias)
                    }
                    return of(item as any as Item)
                })
            )
        }
    }

    public createOffline(body: Partial<Item>) {
        return super.createOffline(body).pipe(
            switchMap(item => {
                if(!(item as any).associatedMedias) {
                    const associatedMedias: AssociatedMedias = {
                        medias: [],
                    }
                    _.set(item as any, 'associatedMedias', associatedMedias)
                }
                this.offlineDataUpdateCacher.storeBeforePushing(item.id, item)
                return of(item)
            })
        )
    }

    public addImage(item: Item, image: { newMediaId: string }) {
        return this.proxy.fetch(`/physical-museum-items/addMedia/${item.id}`,{idMedia: image.newMediaId}, 'PUT')
    }

    public fetchIfHasAccess(item: string) {
        return this.fetchOne(item).pipe(
            catchError((err) => err.status === 403 ? of(null) : err)
        )
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new ItemOfflineDataFetcher(CACHE_KEYS.Item);
    }
}
